import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { isMasterUser } from './authSession';

const CACHE_EVENT = 'acompanha:plan-usage-updated';

/**
 * O uso muda a cada cadastro, então o cache vive em memória (nunca em
 * localStorage) e expira rápido. Várias telas montam o guard ao mesmo tempo,
 * por isso as chamadas simultâneas compartilham a mesma promise.
 */
const CACHE_TTL_MS = 30000;

let cachedUsage = null;
let cachedAt = 0;
let inFlight = null;

function isFresh() {
    return cachedUsage != null && Date.now() - cachedAt < CACHE_TTL_MS;
}

function publish(usage) {
    cachedUsage = usage;
    cachedAt = Date.now();
    window.dispatchEvent(new CustomEvent(CACHE_EVENT, { detail: usage }));
}

/** Descarta o cache para que o próximo guard busque o uso novamente. */
export function invalidatePlanUsage() {
    cachedUsage = null;
    cachedAt = 0;
    inFlight = null;
    window.dispatchEvent(new CustomEvent(CACHE_EVENT, { detail: null }));
}

export function readCachedPlanUsage() {
    return cachedUsage;
}

export async function fetchPlanUsage({ force = false } = {}) {
    if (!force && isFresh()) {
        return cachedUsage;
    }
    if (inFlight) {
        return inFlight;
    }

    inFlight = api.get('/me/plan-usage')
        .then((response) => {
            const usage = response?.data?.data?.usage;
            const next = Array.isArray(usage) ? usage : [];
            publish(next);
            return next;
        })
        .catch(() => {
            // Sem o uso, não bloqueia nada: é melhor deixar passar e o backend
            // recusar do que travar um botão por falha de rede.
            publish([]);
            return [];
        })
        .finally(() => {
            inFlight = null;
        });

    return inFlight;
}

export function findPlanUsageEntry(usage, quotaKey) {
    if (!Array.isArray(usage) || !quotaKey) {
        return null;
    }
    return usage.find((entry) => entry?.key === quotaKey) || null;
}

/**
 * Estado de uma cota para bloquear a ação antes de o usuário abrir o formulário.
 * Enquanto carrega, `blocked` é false para o botão não piscar como travado.
 */
export function usePlanQuota(quotaKey) {
    const [usage, setUsage] = useState(readCachedPlanUsage);
    const [loading, setLoading] = useState(() => readCachedPlanUsage() == null);

    const refresh = useCallback(async ({ force = false } = {}) => {
        setLoading(true);
        try {
            const next = await fetchPlanUsage({ force });
            setUsage(next);
            return next;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!isMasterUser()) {
            fetchPlanUsage().then((next) => {
                if (!cancelled) {
                    setUsage(next);
                }
            }).finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }

        const onUpdated = (event) => {
            if (cancelled) {
                return;
            }
            const detail = event?.detail;
            if (detail == null) {
                // Cache invalidado por um cadastro: busca o número novo.
                fetchPlanUsage({ force: true }).then((next) => {
                    if (!cancelled) {
                        setUsage(next);
                    }
                });
                return;
            }
            setUsage(detail);
        };

        window.addEventListener(CACHE_EVENT, onUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(CACHE_EVENT, onUpdated);
        };
    }, []);

    const entry = findPlanUsageEntry(usage, quotaKey);

    return {
        entry,
        loading,
        blocked: !isMasterUser() && Boolean(entry?.blocked),
        refresh,
    };
}
